/**
 * `/admin/subscribers` — the owner's view of the launch mailing list.
 *
 * There is no Rails route behind this path, the same way there is none behind
 * `/sign-in`. The catch-all serves the SPA shell for every non-XHR HTML path
 * and the router picks the page from there; the credential check happens on the
 * XHR the page then makes, which `Api::V1::Admin::BaseController` answers with
 * the failure app's JSON 401 when there is no operator session.
 *
 * That means the shell for this URL is public. Nothing sensitive is in it — the
 * addresses arrive only with an authenticated response — but it is why the page
 * is marked `noindex` alongside `/sign-in`: an admin surface has no business in
 * a search index even when it renders nothing to a stranger.
 *
 * ## Layout only
 *
 * Everything with a decision in it — paging, what an expired session says, the
 * two-press confirmation in front of a removal — lives in `subscriber-list.tsx`,
 * including this page's one `<h1>`. This file supplies a column and nothing
 * else, the arrangement `sign-in.tsx` and `not-found.tsx` both use: a 1100 px
 * column with no inset of its own wrapping a section that carries `spacing.md`
 * on the horizontal axis, which is the single gutter keeping a 320 px viewport
 * reflowing without horizontal scroll (FR-019(f)).
 *
 * There is no footer, so `role="main"` is the page's only landmark besides the
 * header's `banner`. There is also no link to here from the site header: the
 * marketing chrome is for visitors, and an admin path advertised in it would be
 * an invitation to rattle the handle.
 */

import { createFileRoute } from "@tanstack/react-router";
import { StyleSheet, View } from "react-native";

import { PageColumn } from "@/components/page-column";
import { SubscriberList } from "@/components/operators/subscriber-list";
import { spacing } from "@/theme/tokens";

export const Route = createFileRoute("/admin/subscribers")({
  component: SubscribersPage,
  head: () => ({
    meta: [
      { title: "Mailing list — Sparkie" },
      // Same reasoning as `/sign-in`: a client-rendered hint binds only
      // JS-executing crawlers, and the airtight server-side X-Robots-Tag is out
      // of this SPA's reach and not worth a Rails special case for one page.
      { name: "robots", content: "noindex" },
    ],
  }),
});

function SubscribersPage() {
  return (
    <PageColumn>
      <View role="main" style={styles.section}>
        <SubscriberList />
      </View>
    </PageColumn>
  );
}

const styles = StyleSheet.create({
  section: {
    // Asymmetric like the other short pages': the space above is the page's
    // opening breath, the space below only has to keep the last row off the
    // bottom of the window.
    paddingTop: spacing.xxl,
    paddingBottom: spacing.xxl,
    paddingHorizontal: spacing.md,
  },
});
